/**
 * Battle glass stage (Phaser-wired shell).
 *
 * Builds the three in-canvas layers behind the battler sprites — see
 * battleStageLogic.ts for the composition rationale + geometry:
 *
 *   depth 0, insertion-ordered:
 *     [snapshot Image — hidden once the blur bake succeeds]
 *     blurred snapshot Image (baked downscale chain, stretched full-bleed)
 *     scrim wash Rectangle (SCRIM_WASH)
 *     stage panel Graphics (GLASS_FILL_THIN + SPECULAR_EDGE top inset)
 *   ...then BattleScene.createObjects() adds the sprites above.
 *
 * Ownership: the ORIGINAL snapshot texture + Image stay with the
 * SnapshotHandle (they transfer to EndRunScene on defeat — that scene
 * wants the sharp capture under its own DOM blur). This module owns only
 * the blur-chain DynamicTextures + the three layer GameObjects, and
 * tears them down on the host scene's SHUTDOWN.
 *
 * Zero runtime Phaser import (type-only) — mirrors snapshotBackdrop's
 * pattern so vitest can drive buildBattleStage with a mock scene.
 */

import type { Scene, GameObjects } from 'phaser';
import {
  GLASS_FILL_THIN,
  SPECULAR_EDGE,
  SCRIM_WASH,
} from '../styles/phaserPalette';
import { computeStageLayout, computeBlurChain } from './battleStageLogic';
import type { SnapshotHandle } from './snapshotBackdrop';

// String/enum literals inlined from Phaser 3.90 so this module needs no
// runtime Phaser import (same convention as snapshotBackdrop.ts).
const SCENE_EVENT_SHUTDOWN = 'shutdown';
const FILTER_MODE_LINEAR = 0; // Phaser.Textures.FilterMode.LINEAR

export interface BattleStageHandle {
  /** Re-run geometry — call from BattleScene.layout() with the same
   *  playerSpriteY it computes for the player sprite's bottom edge. */
  layout(width: number, height: number, playerSpriteBottom: number): void;
  /** Idempotent teardown. Also auto-runs on host scene SHUTDOWN. */
  destroy(): void;
}

/**
 * Mount the stage layers onto `scene`. Call AFTER the snapshot handle's
 * transferOwnership(scene) and BEFORE the battler sprites are created,
 * so display-list insertion order stacks the stage behind the fight.
 * Pass `null` when the snapshot capture failed — the wash + panel still
 * render over the flat `#0E1014` camera fill.
 */
export function buildBattleStage(
  scene: Scene,
  snapshot: SnapshotHandle | null,
): BattleStageHandle {
  const width = scene.scale.width;
  const height = scene.scale.height;

  let blurImage: GameObjects.Image | null = null;
  const blurKeys: string[] = [];

  if (snapshot) {
    try {
      // The game boots pixelArt:true (NEAREST everywhere); the blur bake
      // needs LINEAR taps on every level, source included. Neutral for
      // the original snapshot's other consumer (EndRunScene renders it
      // 1:1, where the filter mode is invisible).
      scene.textures.get(snapshot.textureKey).setFilter(FILTER_MODE_LINEAR);
      let prevKey = snapshot.textureKey;
      let prevW = width;
      let prevH = height;
      for (const level of computeBlurChain({ width, height })) {
        const key = `${snapshot.textureKey}${level.keySuffix}`;
        const dt = scene.textures.addDynamicTexture(key, level.width, level.height);
        if (!dt) {
          throw new Error(`addDynamicTexture returned null for ${key}`);
        }
        blurKeys.push(key);
        dt.setFilter(FILTER_MODE_LINEAR);
        // StampConfig only carries uniform `scale`; use cover-scale
        // (max of the two axis ratios) so the stamp always fills the
        // level — the ≤2px overflow on the other axis clips at the
        // texture bounds, whereas an undershoot would leave a
        // transparent gutter that upsampling smears into edge fringe.
        dt.stamp(prevKey, undefined, 0, 0, {
          scale: Math.max(level.width / prevW, level.height / prevH),
          originX: 0,
          originY: 0,
        });
        prevKey = key;
        prevW = level.width;
        prevH = level.height;
      }
      snapshot.image.setVisible(false);
      blurImage = scene.add.image(0, 0, prevKey).setOrigin(0).setDepth(0);
      blurImage.setDisplaySize(width, height);
    } catch (err) {
      // Bake failure degrades to the sharp snapshot — wash + panel still
      // deliver the stage; only the frosting is lost.
      console.warn('[battleStage] blur bake failed; sharp snapshot stays', err);
      snapshot.image.setVisible(true);
      blurImage?.destroy();
      blurImage = null;
      for (const key of blurKeys) {
        if (scene.textures.exists(key)) scene.textures.remove(key);
      }
      blurKeys.length = 0;
    }
  }

  const wash = scene.add
    .rectangle(0, 0, width, height, SCRIM_WASH.hex, SCRIM_WASH.alpha)
    .setOrigin(0)
    .setDepth(0);
  const panel = scene.add.graphics().setDepth(0);

  let destroyed = false;
  const onShutdown = (): void => handle.destroy();
  scene.events.once(SCENE_EVENT_SHUTDOWN, onShutdown);

  const handle: BattleStageHandle = {
    layout(w: number, h: number, playerSpriteBottom: number): void {
      if (destroyed) return;
      const stage = computeStageLayout({ width: w, height: h, playerSpriteBottom });
      blurImage?.setDisplaySize(w, h);
      wash.setSize(w, h);
      panel.clear();
      panel.fillStyle(GLASS_FILL_THIN.hex, GLASS_FILL_THIN.alpha);
      panel.fillRoundedRect(
        stage.panel.x,
        stage.panel.y,
        stage.panel.width,
        stage.panel.height,
        stage.panel.radius,
      );
      panel.lineStyle(1, SPECULAR_EDGE.hex, SPECULAR_EDGE.alpha);
      panel.beginPath();
      panel.moveTo(stage.specular.x1, stage.specular.y);
      panel.lineTo(stage.specular.x2, stage.specular.y);
      panel.strokePath();
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      scene.events.off(SCENE_EVENT_SHUTDOWN, onShutdown);
      blurImage?.destroy();
      blurImage = null;
      wash.destroy();
      panel.destroy();
      const game = scene.game;
      for (const key of blurKeys) {
        if (game?.textures?.exists(key)) game.textures.remove(key);
      }
    },
  };

  return handle;
}
